import { useEffect, useRef, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { SearchCandidateProfileMessageSender } from 'generated-graphql/types';
import { SearchCandidateVectorQueryMessage } from 'modules/candidate-profile/types/SearchCandidateVectorQueryMessage';
import { GetSearchCandidateProfileQueryQuery } from 'generated-graphql/hooks/apollo-hooks';
import { extractQueryGetItem } from 'core/utils/apollo/extractGetItem';

type ConversationHistoryState = {
  conversationHistory: SearchCandidateVectorQueryMessage[];
  placeholderMessage: SearchCandidateVectorQueryMessage | null;
};

type UseSearchCandidateProfileQueryConversationHistoryProps = {
  searchQueryId: string | null;
  queryData: GetSearchCandidateProfileQueryQuery | undefined;
  queryLoading: boolean;
  queryError?: ApolloError;
};

/**
 * Manages conversation history state including loading from backend,
 * adding messages, resetting, and regenerating from a specific point.
 */
export const useSearchCandidateProfileQueryConversationHistory = ({
  searchQueryId,
  queryData,
  queryLoading,
  queryError
}: UseSearchCandidateProfileQueryConversationHistoryProps) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [state, setState] = useState<ConversationHistoryState>({
    conversationHistory: [],
    placeholderMessage: null
  });

  const searchCandidateProfileQuery =
    queryData !== undefined ? extractQueryGetItem(queryData) : undefined;

  const previousSearchQueryIdRef = useRef(searchQueryId);

  // Opening another saved search starts fresh, so the one on screen goes
  // before the new one loads rather than lingering beside its filters.
  useEffect(() => {
    const previousSearchQueryId = previousSearchQueryIdRef.current;
    previousSearchQueryIdRef.current = searchQueryId;

    if (
      previousSearchQueryId &&
      searchQueryId &&
      previousSearchQueryId !== searchQueryId
    ) {
      setState({ conversationHistory: [], placeholderMessage: null });
      setIsInitialized(false);
    }
  }, [searchQueryId]);

  // Initialize conversation history from backend when searchQueryId exists
  useEffect(() => {
    if (searchCandidateProfileQuery) {
      const messages =
        searchCandidateProfileQuery.searchCandidateProfileMessages?.items || [];
      const conversationHistory: SearchCandidateVectorQueryMessage[] = messages
        .filter((message) => message !== null)
        .map((message) => ({
          id: message.id,
          body: message.body,
          sender: message.sender,
          date: message.createdAt
        }))
        .sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );
      setState({
        conversationHistory,
        placeholderMessage: null
      });
      setIsInitialized(true);
    } else if (!searchQueryId) {
      setIsInitialized(true);
    } else if (searchQueryId && !queryLoading && queryError) {
      // A failed fetch says nothing about the conversation already on screen:
      // a first search adds its messages before the id reaches the URL.
      setIsInitialized(true);
    } else if (
      searchQueryId &&
      !queryLoading &&
      queryData !== undefined &&
      !searchCandidateProfileQuery
    ) {
      // Query completed but returned null/undefined - reset state
      setState({
        conversationHistory: [],
        placeholderMessage: null
      });
      setIsInitialized(true);
    }
  }, [
    searchCandidateProfileQuery,
    searchQueryId,
    queryLoading,
    queryData,
    queryError
  ]);

  const addMessages = (
    newMessages: SearchCandidateVectorQueryMessage[]
  ): void => {
    setState((prev) => {
      // Filter out duplicates by ID
      const existingIds = new Set(
        prev.conversationHistory.map((msg) => msg.id)
      );
      const uniqueNewMessages = newMessages.filter(
        (msg) => !existingIds.has(msg.id)
      );

      return {
        ...prev,
        conversationHistory: [
          ...prev.conversationHistory,
          ...uniqueNewMessages
        ],
        placeholderMessage: null
      };
    });
  };

  const setPlaceholder = (
    message: SearchCandidateVectorQueryMessage | null
  ): void => {
    setState((prev) => ({
      ...prev,
      placeholderMessage: message
    }));
  };

  const resetHistory = (): void => {
    setState({
      conversationHistory: [],
      placeholderMessage: null
    });
  };

  const regenerateFromIndex = (
    agentMessageId: string
  ): {
    success: boolean;
    userMessageBody: string | null;
    removedMessageIds: string[];
  } => {
    const messageIndex = state.conversationHistory.findIndex(
      (msg) => msg.id === agentMessageId
    );

    if (messageIndex === -1 || messageIndex === 0) {
      return { success: false, userMessageBody: null, removedMessageIds: [] };
    }

    // Find the user message before this agent message
    const previousMessage = state.conversationHistory[messageIndex - 1];

    if (
      !previousMessage ||
      previousMessage.sender !== SearchCandidateProfileMessageSender.User
    ) {
      return { success: false, userMessageBody: null, removedMessageIds: [] };
    }

    // Get message IDs that will be removed (user message before agent message and all after)
    const removedMessageIds = state.conversationHistory
      .slice(messageIndex - 1)
      .map((msg) => msg.id);

    // Keep all messages before the user message that prompted this agent response
    const newConversationHistory = state.conversationHistory.slice(
      0,
      messageIndex - 1
    );

    setState({
      conversationHistory: newConversationHistory,
      placeholderMessage: null
    });

    return {
      success: true,
      userMessageBody: previousMessage.body,
      removedMessageIds
    };
  };

  return {
    conversationHistory: state.conversationHistory,
    placeholderMessage: state.placeholderMessage,
    isInitialized,
    addMessages,
    setPlaceholder,
    resetHistory,
    regenerateFromIndex
  };
};
